/**
 * imageUtils
 *
 * ビデオ映像→Claude APIに渡せるbase64画像へ変換するユーティリティ。
 *
 * コスト削減のため、画像は必ずここを通してリサイズ・圧縮する。
 *   - 長辺 1024px にリサイズ
 *   - JPEG 品質 75%
 *   - PNGではなくJPEG固定（転送量・APIコスト両面で有利）
 */

/** デフォルト設定（CLAUDE.md コスト意識セクションに準拠） */
const DEFAULT_MAX_LONG_SIDE = 1024;
const DEFAULT_JPEG_QUALITY = 0.75;

export interface CapturedFrame {
  /** Claude APIに渡せるbase64文字列（"data:..." のプレフィックスは含まない） */
  base64: string;
  /** 出力画像の幅（px） */
  width: number;
  /** 出力画像の高さ（px） */
  height: number;
}

/**
 * 与えられた幅・高さを「長辺 maxLongSide」に収まるサイズへ縮める
 * 元が小さければそのまま返す
 */
function fitToMaxLongSide(width: number, height: number, maxLongSide: number) {
  const longSide = Math.max(width, height);
  if (longSide <= maxLongSide) {
    return { width, height };
  }
  const scale = maxLongSide / longSide;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

/**
 * data URL（"data:image/jpeg;base64,xxx"）からbase64部分だけを取り出す
 */
function stripDataUrlPrefix(dataUrl: string): string {
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx < 0) return dataUrl;
  return dataUrl.slice(commaIdx + 1);
}

/**
 * <video> 要素から現在表示中のフレームをキャプチャしてJPEG/base64に変換
 *
 * @throws video が再生開始前（width/heightが0）の場合
 */
export function captureFrameFromVideo(
  video: HTMLVideoElement,
  options?: {
    maxLongSide?: number;
    jpegQuality?: number;
  },
): CapturedFrame {
  const maxLongSide = options?.maxLongSide ?? DEFAULT_MAX_LONG_SIDE;
  const jpegQuality = options?.jpegQuality ?? DEFAULT_JPEG_QUALITY;

  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;

  if (sourceWidth === 0 || sourceHeight === 0) {
    throw new Error('カメラ映像がまだ準備できていません。少し待ってから再試行してください。');
  }

  const { width, height } = fitToMaxLongSide(sourceWidth, sourceHeight, maxLongSide);

  // OffscreenCanvas はSafariで対応がまちまちなので、確実な普通のcanvasを使う
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvasの2Dコンテキストを取得できませんでした。');
  }

  // 縮小描画（drawImageが内部でバイリニア補間してくれる）
  ctx.drawImage(video, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
  const base64 = stripDataUrlPrefix(dataUrl);

  return { base64, width, height };
}
